import { Link } from "react-router-dom";
import { Target, Eye, Heart, ArrowRight } from "lucide-react";

const IMAGES = {
  campus: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/yjp5nd00_WhatsApp%20Image%202026-03-04%20at%204.04.56%20PM.jpeg",
  culinary: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/4e3jh4f6_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM.jpeg",
  frontDesk: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/2hjnk04u_WhatsApp%20Image%202026-03-04%20at%204.01.13%20PM.jpeg",
  lobby: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/pbcdfpgm_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM%20%282%29.jpeg",
  awards: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/d3dbw33j_WhatsApp%20Image%202026-03-04%20at%204.04.57%20PM%20%281%29.jpeg",
  teachersDay: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/ialfv394_cover%20page.jpg",
  bartending: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/vrnanmho_WhatsApp%20Image%202026-03-04%20at%204.01.14%20PM%20%281%29.jpeg",
  director: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/qjakkrn3_IMG-20260228-WA0010.jpg",
};

const values = [
  { icon: Target, title: "Our Mission", text: "To provide world-class hospitality education that empowers students with practical skills, professional ethics, and global perspectives to excel in the dynamic hospitality industry." },
  { icon: Eye, title: "Our Vision", text: "To be the most respected hotel management institution in South India, recognized for producing industry-ready professionals who lead with innovation and excellence." },
  { icon: Heart, title: "Our Values", text: "Integrity, Excellence, Innovation, Inclusivity, and a deep commitment to nurturing the next generation of hospitality leaders." },
];

const facilities = [
  { title: "Professional Culinary Kitchens", desc: "Fully equipped commercial-grade kitchens with industrial exhaust systems where students master cuisines from around the world.", img: IMAGES.culinary },
  { title: "F&B Service & Restaurant Simulation", desc: "Complete restaurant simulation with bar setup for hands-on training in food and beverage service excellence.", img: IMAGES.frontDesk },
  { title: "Bartending & Mixology Lab", desc: "Professional bar lab with fully stocked shelves where students learn bartending, cocktail making, and bar management.", img: IMAGES.bartending },
  { title: "Housekeeping Training Suite", desc: "Fully furnished hotel rooms for practical housekeeping training, room service, and guest accommodation management.", img: IMAGES.lobby },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Banner */}
      <section className="relative pt-36 pb-20">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.campus})` }} />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <p className="font-body text-sm uppercase tracking-[0.3em] text-white/70 mb-3">About Us</p>
          <h1 data-testid="about-hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Our Story
          </h1>
          <p className="font-body text-lg text-white/80 max-w-2xl">
            A legacy of excellence in hospitality education, shaping careers since our establishment in Hyderabad.
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section data-testid="values-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <div key={v.title} data-testid={`value-card-${i}`} className="p-8 bg-slate-50 border-t-4 border-crimson">
                <div className="w-12 h-12 flex items-center justify-center bg-crimson/10 text-crimson mb-5">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-navy mb-4">{v.title}</h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section data-testid="story-section" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Our Legacy</p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight mb-6">
                Building Hospitality Leaders Since Day One
              </h2>
              <div className="space-y-4 font-body text-base text-slate-600 leading-relaxed">
                <p>
                  Vainavi Educational Institutions was founded with a singular vision: to create a world-class hotel management college in the heart of Hyderabad that bridges the gap between academic learning and industry demands.
                </p>
                <p>
                  Located in Moosapet at M.S.P-6, 11-5-150/C, Moosapet Rd, Bhavani Nagar, our institution has grown from a modest beginning into one of the most sought-after hospitality education centers in Telangana. Our comprehensive programs, designed in collaboration with leading hotel chains, ensure that every graduate is industry-ready from day one.
                </p>
                <p>
                  With over 5,000 successful alumni now working across prestigious hotels worldwide, from the Taj Group to international chains like Marriott and Hilton, we take pride in our track record of transforming aspiring students into accomplished hospitality professionals.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src={IMAGES.teachersDay} alt="Vainavi Students" className="w-full h-96 object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-crimson text-white p-6">
                <p className="font-heading text-3xl font-bold">9+</p>
                <p className="font-body text-sm uppercase tracking-wider">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Director */}
      <section data-testid="leadership-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Leadership</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight">
              Our Director
            </h2>
            <div className="w-16 h-1 bg-crimson mx-auto mt-4" />
          </div>

          {/* Director - Two Column Layout */}
          <div data-testid="director-message" className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Left Side - Director Image (40%) */}
            <div className="lg:col-span-2 flex justify-center">
              <div
                data-testid="director-card"
                className="w-full max-w-md overflow-hidden rounded-lg shadow-lg"
              >
                <img
                  src={IMAGES.director}
                  alt="Mr. Vaddeboina Varun - Director, Vainavi College of Hotel Management"
                  className="w-full aspect-[3/4] object-cover object-top"
                  data-testid="director-image"
                />
              </div>
            </div>

            {/* Right Side - Director's Message (60%) */}
            <div className="lg:col-span-3">
              <div className="border-l-4 border-crimson pl-6 mb-8">
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy">
                  Director&apos;s Message
                </h3>
              </div>
              <div className="space-y-5 font-body text-base text-slate-600 leading-relaxed">
                <p>
                  It gives me great pleasure to welcome you to Vainavi College of Hotel Management, an institution dedicated to excellence in hospitality education and professional growth. Hospitality is more than just a profession—it is a commitment to service, creativity, discipline, and passion.
                </p>
                <p>
                  At Vainavi College of Hotel Management, our mission is to shape skilled, confident, and industry-ready professionals who are prepared to meet global standards. We focus on providing quality education that blends strong academic foundations with practical expertise. Our goal is to equip students not only with technical knowledge but also with leadership qualities, professional ethics, and a positive attitude that will help them excel in their careers.
                </p>
                <p>
                  Our programs are carefully structured to ensure a balance between classroom learning and hands-on training. With experienced faculty, modern facilities, and a supportive learning environment, we strive to create opportunities for students to explore their talents and achieve their full potential.
                </p>
                <p>
                  The hospitality industry is dynamic and ever-evolving, and success comes to those who are adaptable, dedicated, and continuously learning. We encourage our students to stay motivated, embrace challenges, and confidently pursue their ambitions.
                </p>
                <p className="font-medium text-navy">
                  I warmly invite you to join Vainavi College of Hotel Management and begin your journey toward a successful and rewarding career in the world of hospitality.
                </p>
              </div>

              {/* Name & Designation */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="font-heading text-lg font-bold text-navy" data-testid="director-name">
                  Mr. Vaddeboina Varun
                </p>
                <p className="font-body text-sm font-medium text-crimson mt-1">
                  Director
                </p>
                <p className="font-body text-sm text-slate-500 mt-0.5">
                  Vainavi College of Hotel Management
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Facilities */}
      <section data-testid="facilities-section" className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="font-body text-sm uppercase tracking-[0.3em] text-crimson mb-3">Infrastructure</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy tracking-tight">
              Campus Facilities
            </h2>
            <div className="w-16 h-1 bg-crimson mx-auto mt-4" />
          </div>
          <div className="space-y-16">
            {facilities.map((fac, i) => (
              <div
                key={fac.title}
                data-testid={`facility-block-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
              >
                <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <img src={fac.img} alt={fac.title} className="w-full h-80 object-cover" />
                </div>
                <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <h3 className="font-heading text-2xl md:text-3xl font-medium text-navy mb-4">{fac.title}</h3>
                  <p className="font-body text-base text-slate-600 leading-relaxed">{fac.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Ready to Visit Our Campus?</h2>
          <p className="font-body text-lg text-white/80 mb-8">Schedule a campus tour and experience our world-class facilities firsthand.</p>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="inline-flex items-center gap-2 bg-crimson text-white px-10 py-4 font-body text-sm font-semibold uppercase tracking-wider hover:bg-crimson-hover transition-all duration-300"
          >
            Schedule a Visit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
